import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  SimpleChanges,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { FormGroup } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { Subscription, finalize } from 'rxjs';
import jsonata from 'jsonata';

import { IAction } from '../../models/workflow-builder.model';
import { extractNotificationVariables } from '../../core/notification-editor';
// Reuse the SAME Formly field definitions the v1 editor uses for action args
// (notification templates en/fr/kin, office assignment, BILL_ID, certificate…).
import { getStateActionFields } from '../../../irembo-workflow-editor/edit-transition/fields';
import { environment } from '../../../../../../environments/environment';

const clone = <T>(v: T): T => (v == null ? v : JSON.parse(JSON.stringify(v)));

const LIST_ARG_KEYS = [
  'formFieldKeys',
  'internalEmailNotificationRecipients',
  'internalSmsNotificationRecipients',
  'formFieldsSmsNotificationRecipients',
  'formFieldsEmailNotificationRecipients',
];

const STRING_MAP_ARG_KEYS = [
  'certificateNames',
  'parametersToRetain',
  'datasetFieldsMapping',
];

const JSON_ARG_KEYS = ['attachment', 'reminderNotifications'];
const OBJECT_BOUND_ARG_KEYS = ['certificateTemplateCode', 'officeId'];
const NUMBER_ARG_KEYS = ['reminderOffsetDays', 'certificateExpirationDays'];
const REQUEST_SAMPLE_MAPPING_KEYS = new Set([
  'path',
  'query',
  'headers',
  'body',
  'form',
]);
const NOTIFICATION_BACKED_ACTIONS = [
  'NOTIFICATION',
  'INTERNAL_NOTIFICATION',
  'OFFICE_ASSIGNMENT_NOTIFICATION',
];
const OFFICE_BACKED_ACTIONS = [
  'OFFICE_ASSIGNMENT',
  'OFFICE_ASSIGNMENT_NOTIFICATION',
];

const integrationEndpointMappingUrl = (
  endpointCode: string,
  suffix = ''
): string =>
  `${
    environment.apiGatewayBaseUrl
  }/integration/v1/endpoints/${encodeURIComponent(
    endpointCode
  )}/mapping${suffix}`;

const integrationEndpointDetailUrl = (endpointCode: string): string =>
  `${
    environment.apiGatewayBaseUrl
  }/integration/v1/endpoints/${encodeURIComponent(endpointCode)}/get`;

const splitTextList = (value: string): string[] =>
  value
    .split(/[\n,]/)
    .map(item => item.trim())
    .filter(Boolean);

const parseJsonOrKeyValueMap = (value: string): Record<string, string> => {
  const trimmed = value.trim();
  if (!trimmed) return {};
  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed
        : {};
    } catch {
      return {};
    }
  }

  return trimmed.split(/\n/).reduce<Record<string, string>>((acc, line) => {
    const separator = line.includes('=') ? '=' : ':';
    const [key, ...valueParts] = line.split(separator);
    const cleanKey = key?.trim();
    const cleanValue = valueParts.join(separator).trim();
    if (cleanKey && cleanValue) acc[cleanKey] = cleanValue;
    return acc;
  }, {});
};

const stringifyForEditor = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.join('\n');
  if (value && typeof value === 'object') return JSON.stringify(value, null, 2);
  return value;
};

const selectedCertificateCode = (
  template: Record<string, unknown>
): string | null =>
  String(
    template['code'] ??
      template['certificateTemplateCode'] ??
      template['templateCode'] ??
      template['name'] ??
      ''
  ).trim() || null;

const selectedCertificateName = (
  template: Record<string, unknown>,
  fallback: string
): string =>
  String(
    template['certificateTemplateName'] ??
      template['certificateName'] ??
      template['title'] ??
      template['name'] ??
      fallback
  ).trim();

const selectedCertificateLocale = (template: Record<string, unknown>): string =>
  String(
    template['templateLanguage'] ??
      template['language'] ??
      template['locale'] ??
      'en-US'
  ).trim() || 'en-US';

const normalizeCertificateTemplateSelection = (
  value: Record<string, unknown>
): {
  templateCodes: Record<string, string>;
  templateNames: Record<string, string>;
} | null => {
  const directCode = selectedCertificateCode(value);
  if (directCode) {
    const locale = selectedCertificateLocale(value);
    return {
      templateCodes: { [locale]: directCode },
      templateNames: {
        [locale]: selectedCertificateName(value, directCode),
      },
    };
  }

  const templateCodes: Record<string, string> = {};
  const templateNames: Record<string, string> = {};
  Object.entries(value).forEach(([locale, template]) => {
    if (template && typeof template === 'object' && !Array.isArray(template)) {
      const pickedTemplate = template as Record<string, unknown>;
      const code = selectedCertificateCode(pickedTemplate);
      if (!code) return;
      templateCodes[locale] = code;
      templateNames[locale] = selectedCertificateName(pickedTemplate, code);
      return;
    }
    if (typeof template === 'string' && template.trim()) {
      templateCodes[locale] = template.trim();
    }
  });

  return Object.keys(templateCodes).length
    ? { templateCodes, templateNames }
    : null;
};

const selectedOfficeLevel = (office: unknown): string | null => {
  if (!office || typeof office !== 'object' || Array.isArray(office)) {
    return null;
  }
  const pickedOffice = office as Record<string, unknown>;
  return (
    String(
      pickedOffice['officeLevel'] ??
        pickedOffice['level'] ??
        pickedOffice['office_level'] ??
        ''
    ).trim() || null
  );
};

const selectedOfficeId = (office: unknown): string | null => {
  if (typeof office === 'string') return office.trim() || null;
  if (!office || typeof office !== 'object' || Array.isArray(office)) {
    return null;
  }
  const pickedOffice = office as Record<string, unknown>;
  return String(pickedOffice['id'] ?? '').trim() || null;
};

export const prepareWorkflowActionArgsForForm = (
  args: Record<string, unknown>
): Record<string, unknown> => {
  const prepared = clone(args);
  if (typeof prepared['certificateNames'] === 'string') {
    prepared['certificateNames'] = parseJsonOrKeyValueMap(
      prepared['certificateNames'] as string
    );
  }
  const certificateTemplateCode = prepared['certificateTemplateCode'];
  const certificateNames = prepared['certificateNames'];
  if (
    certificateTemplateCode &&
    typeof certificateTemplateCode === 'object' &&
    !Array.isArray(certificateTemplateCode) &&
    Object.values(certificateTemplateCode).every(
      value => typeof value === 'string'
    )
  ) {
    const codesByLocale = certificateTemplateCode as Record<string, string>;
    const namesByLocale =
      certificateNames &&
      typeof certificateNames === 'object' &&
      !Array.isArray(certificateNames)
        ? (certificateNames as Record<string, string>)
        : {};
    const [locale, code] =
      Object.entries(codesByLocale)[0] || (['en-US', ''] as const);
    if (code) {
      prepared['certificateTemplateCode'] = {
        code,
        templateLanguage: locale,
        certificateTemplateName: namesByLocale[locale] || code,
      };
      if (!prepared['certificateName']) {
        prepared['certificateName'] = namesByLocale[locale] || code;
      }
    }
  }
  [...LIST_ARG_KEYS, ...STRING_MAP_ARG_KEYS, ...JSON_ARG_KEYS].forEach(key => {
    if (OBJECT_BOUND_ARG_KEYS.includes(key)) return;
    if (prepared[key] !== undefined && prepared[key] !== null) {
      prepared[key] = stringifyForEditor(prepared[key]);
    }
  });
  return prepared;
};

export const applyWorkflowActionArgDefaults = (
  actionType: string,
  args: Record<string, unknown>
): Record<string, unknown> => {
  if (actionType === 'INTEGRATION' && args['async'] === undefined) {
    return { ...args, async: true };
  }
  if (actionType === 'GENERATE_CERTIFICATE') {
    const expirationDays = Number(args['certificateExpirationDays'] ?? 0);
    return {
      ...args,
      certificateExpires:
        args['certificateExpires'] !== undefined
          ? args['certificateExpires']
          : expirationDays > 0 || !!args['reminderNotifications'],
    };
  }
  return args;
};

export const normalizeWorkflowActionArgs = (
  args: Record<string, unknown>,
  actionType = ''
): Record<string, unknown> => {
  const normalized = clone(args);
  if (actionType === 'INTEGRATION') {
    delete normalized['parametersToRetain'];
  }

  if ('certificateExpires' in normalized) {
    const certificateExpires =
      normalized['certificateExpires'] === true ||
      normalized['certificateExpires'] === 'true';
    delete normalized['certificateExpires'];
    if (!certificateExpires) {
      normalized['certificateExpirationDays'] = 0;
      delete normalized['reminderOffsetDays'];
      delete normalized['reminderNotifications'];
    } else if (
      normalized['certificateExpirationDays'] === undefined ||
      normalized['certificateExpirationDays'] === null ||
      normalized['certificateExpirationDays'] === '' ||
      Number(normalized['certificateExpirationDays']) <= 0
    ) {
      normalized['certificateExpirationDays'] = 365;
    }
  }

  const office = normalized['officeId'];
  if (office && typeof office === 'object' && !Array.isArray(office)) {
    const pickedOffice = office as Record<string, unknown>;
    normalized['officeId'] = pickedOffice['id'] ?? null;
    normalized['officeLevel'] =
      selectedOfficeLevel(pickedOffice) ?? normalized['officeLevel'] ?? null;
    if (pickedOffice['code'] && !normalized['officeCode']) {
      normalized['officeCode'] = pickedOffice['code'];
    }
  }

  const certificateTemplate = normalized['certificateTemplateCode'];
  if (
    certificateTemplate &&
    typeof certificateTemplate === 'object' &&
    !Array.isArray(certificateTemplate)
  ) {
    const pickedTemplate = certificateTemplate as Record<string, unknown>;
    const selection = normalizeCertificateTemplateSelection(pickedTemplate);
    if (selection) {
      const firstName = Object.values(selection.templateNames)[0];
      normalized['certificateTemplateCode'] = selection.templateCodes;
      if (!String(normalized['certificateName'] ?? '').trim()) {
        normalized['certificateName'] =
          firstName || Object.values(selection.templateCodes)[0];
      }
      if (
        !normalized['certificateNames'] &&
        Object.keys(selection.templateNames).length
      ) {
        normalized['certificateNames'] = selection.templateNames;
      }
    }
  } else if (
    typeof certificateTemplate === 'string' &&
    certificateTemplate.trim()
  ) {
    normalized['certificateTemplateCode'] = {
      'en-US': certificateTemplate.trim(),
    };
  }

  if (
    (!normalized['certificateName'] ||
      !String(normalized['certificateName']).trim()) &&
    normalized['certificateNames'] &&
    typeof normalized['certificateNames'] === 'object' &&
    !Array.isArray(normalized['certificateNames'])
  ) {
    normalized['certificateName'] =
      Object.values(normalized['certificateNames'] as Record<string, unknown>)
        .map(value => String(value ?? '').trim())
        .find(Boolean) || '';
  }

  LIST_ARG_KEYS.forEach(key => {
    const value = normalized[key];
    if (typeof value === 'string') normalized[key] = splitTextList(value);
  });

  STRING_MAP_ARG_KEYS.forEach(key => {
    const value = normalized[key];
    if (typeof value === 'string') {
      normalized[key] = parseJsonOrKeyValueMap(value);
    }
  });

  if (
    (!normalized['certificateName'] ||
      !String(normalized['certificateName']).trim()) &&
    normalized['certificateNames'] &&
    typeof normalized['certificateNames'] === 'object' &&
    !Array.isArray(normalized['certificateNames'])
  ) {
    normalized['certificateName'] =
      Object.values(normalized['certificateNames'] as Record<string, unknown>)
        .map(value => String(value ?? '').trim())
        .find(Boolean) || '';
  }

  JSON_ARG_KEYS.forEach(key => {
    const value = normalized[key];
    if (typeof value === 'string' && value.trim()) {
      try {
        normalized[key] = JSON.parse(value);
      } catch {
        normalized[key] = value;
      }
    }
  });

  NUMBER_ARG_KEYS.forEach(key => {
    const value = normalized[key];
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) normalized[key] = parsed;
    }
  });

  return normalized;
};

const setSamplePayloadPath = (
  target: Record<string, unknown>,
  path: string[]
): void => {
  let cursor = target;
  path.forEach((segment, index) => {
    if (index === path.length - 1) {
      if (cursor[segment] === undefined) {
        cursor[segment] = `<${path.join('.')}>`;
      }
      return;
    }
    if (
      !cursor[segment] ||
      typeof cursor[segment] !== 'object' ||
      Array.isArray(cursor[segment])
    ) {
      cursor[segment] = {};
    }
    cursor = cursor[segment] as Record<string, unknown>;
  });
};

const collectJsonataInputPaths = (node: unknown, paths: string[][]): void => {
  if (!node) return;
  if (Array.isArray(node)) {
    node.forEach(item => collectJsonataInputPaths(item, paths));
    return;
  }
  if (typeof node !== 'object') return;

  const current = node as Record<string, unknown>;
  if (current['type'] === 'path' && Array.isArray(current['steps'])) {
    const path = current['steps']
      .filter(
        step =>
          step &&
          typeof step === 'object' &&
          (step as Record<string, unknown>)['type'] === 'name'
      )
      .map(step => String((step as Record<string, unknown>)['value'] || ''))
      .filter(Boolean);
    if (path.length) paths.push(path);
  }

  Object.values(current).forEach(value =>
    collectJsonataInputPaths(value, paths)
  );
};

const collectJsonataRequestInputPaths = (
  ast: unknown,
  paths: string[][]
): void => {
  if (!ast || typeof ast !== 'object' || Array.isArray(ast)) {
    collectJsonataInputPaths(ast, paths);
    return;
  }

  const root = ast as Record<string, unknown>;
  const pairs = root['lhs'];
  const requestExpressions =
    root['type'] === 'unary' && root['value'] === '{' && Array.isArray(pairs)
      ? pairs
          .filter(pair => {
            if (!Array.isArray(pair)) return false;
            const key = pair[0] as Record<string, unknown> | undefined;
            return (
              key?.['type'] === 'string' &&
              REQUEST_SAMPLE_MAPPING_KEYS.has(String(key['value']))
            );
          })
          .map(pair => (pair as unknown[])[1])
      : [];

  if (requestExpressions.length) {
    requestExpressions.forEach(expression =>
      collectJsonataInputPaths(expression, paths)
    );
    return;
  }

  collectJsonataInputPaths(ast, paths);
};

const integrationMappingInputPaths = (mapping: string): string[][] => {
  if (!mapping.trim()) return [];
  try {
    const expression = jsonata(mapping) as unknown as {
      ast?: () => unknown;
    };
    const ast = expression.ast?.();
    const paths: string[][] = [];
    collectJsonataRequestInputPaths(ast, paths);
    const seen = new Set<string>();
    return paths.filter(path => {
      const key = path.join('.');
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  } catch {
    return [];
  }
};

export const buildIntegrationMappingSamplePayload = (
  mapping: string
): string => {
  try {
    const paths = integrationMappingInputPaths(mapping);
    const sample = paths.reduce<Record<string, unknown>>((acc, path) => {
      setSamplePayloadPath(acc, path);
      return acc;
    }, {});
    return Object.keys(sample).length
      ? JSON.stringify(sample, null, 2)
      : '{\n  \n}';
  } catch {
    return '{\n  \n}';
  }
};

/**
 * Renders the rich, reused Formly form for ONE action's args, scoped to the
 * action's type (the `actionType` model drives the field hide-expressions, so
 * only the relevant action's fields show). Emits the edited args back up.
 */
@Component({
  selector: 'irembogov-iwb-action-args-form',
  template: `
    <div class="iwb-inspector-tabs">
      <button
        type="button"
        class="iwb-itab"
        [class.on]="!jsonMode"
        (click)="setMode(false)">
        <i class="fa-solid fa-sliders"></i><span>Form</span>
      </button>
      <button
        type="button"
        class="iwb-itab"
        [class.on]="jsonMode"
        (click)="setMode(true)">
        <i class="fa-solid fa-code"></i><span>JSON</span>
      </button>
    </div>
    <form *ngIf="!jsonMode" [formGroup]="form" class="iwb-formly">
      <div
        *ngIf="showCertificateRefresh"
        class="iwb-template-select-row"
        aria-label="Certificate templates fetch controls">
        <span>Certificate templates are fetched from Document Generation.</span>
        <button
          type="button"
          class="iwb-template-refresh"
          title="Refresh certificate templates"
          (click)="refreshCertificateTemplates()">
          <i class="fa-solid fa-rotate-right"></i>
        </button>
      </div>
      <formly-form
        [form]="form"
        [fields]="fields"
        [model]="model"
        [options]="options"></formly-form>
      <div class="iwb-integration-mapping" *ngIf="showIntegrationMappingPanel">
        <div class="iwb-integration-mapping-head">
          <div>
            <b>Endpoint mapping</b>
            <small
              >Fetch the saved JSONata mapping and evaluate sample input.</small
            >
          </div>
          <button
            type="button"
            class="iwb-template-refresh"
            [disabled]="integrationMappingLoading || !selectedEndpointCode"
            title="Fetch mapping"
            (click)="fetchIntegrationMapping()">
            <i class="fa-solid fa-rotate-right"></i>
          </button>
        </div>
        <div class="dnone" *ngIf="!selectedEndpointCode">
          Select an endpoint first.
        </div>
        <div
          class="dnone"
          style="color:#b42318"
          *ngIf="integrationMappingError">
          {{ integrationMappingError }}
        </div>
        <div class="dnone" *ngIf="integrationEndpointAdapterCode">
          Adapter:
          <b>{{ integrationEndpointAdapterCode }}</b>
        </div>
        <ngx-monaco-editor
          *ngIf="isSelectedEndpointJsonataCompatible"
          class="iwb-integration-mapping-editor"
          [options]="mappingEditorOptions"
          [ngModel]="integrationMappingJson"
          [ngModelOptions]="{ standalone: true }"></ngx-monaco-editor>
        <div class="dnone" *ngIf="isSelectedEndpointJsonataCompatible">
          Mapping is read-only here. Update it in Integration Management.
        </div>
        <div
          class="iwb-integration-variable-warning"
          *ngIf="integrationMappingUnknownVariables.length">
          <b>Mapping uses unavailable values</b>
          <p>
            These input values are referenced by the endpoint mapping, but they
            are not available from this form or the global service payload.
          </p>
          <div class="iwb-integration-variable-list">
            <code *ngFor="let variable of integrationMappingUnknownVariables">
              {{ variable }}
            </code>
          </div>
        </div>
        <div class="dfield">
          <label>Sample payload</label>
          <ngx-monaco-editor
            class="iwb-integration-payload-editor"
            [options]="jsonEditorOptions"
            [(ngModel)]="integrationPreviewInput"
            [ngModelOptions]="{ standalone: true }"
            (ngModelChange)="integrationPreviewError = ''"></ngx-monaco-editor>
        </div>
        <button
          type="button"
          class="btn drawer-add"
          [disabled]="!canEvaluateIntegrationMapping"
          (click)="testIntegrationMapping()">
          <i class="fa-solid fa-play"></i> Evaluate mapping
        </button>
        <div
          class="dnone"
          style="color:#b42318"
          *ngIf="integrationPreviewError">
          {{ integrationPreviewError }}
        </div>
        <ngx-monaco-editor
          *ngIf="integrationPreviewJson"
          class="iwb-integration-payload-editor"
          [options]="jsonEditorOptions"
          [(ngModel)]="integrationPreviewJson"
          [ngModelOptions]="{ standalone: true }"></ngx-monaco-editor>
      </div>
    </form>
    <div *ngIf="jsonMode">
      <ngx-monaco-editor
        class="iwb-statejson-editor"
        [options]="jsonEditorOptions"
        [(ngModel)]="jsonDraft"
        (ngModelChange)="jsonError = ''"></ngx-monaco-editor>
      <div class="dnone" style="color:#b42318" *ngIf="jsonError">
        {{ jsonError }}
      </div>
    </div>
  `,
})
export class ActionArgsFormComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  @Input() action!: IAction;
  /** Org id — lets the OFFICE_ASSIGNMENT "Office ID" become a backend office picker. */
  @Input() organizationId?: string;
  @Input() formFields?: unknown;

  form = new FormGroup({});
  model: { actionType: string; args: Record<string, unknown> } = {
    actionType: '',
    args: {},
  };
  fields: FormlyFieldConfig[] = [];
  // The reused field wrappers (readonly / rfa-readonly / builder toolbar) gate on
  // formState; without it they render the fields READONLY. Mirror the v1 editor.
  options: FormlyFormOptions = {};

  // per-action raw-JSON editing (configure each action by its own JSON)
  jsonMode = false;
  jsonDraft = '';
  jsonError = '';
  // Monaco options — same look as the state JSON tab / form builder field editor.
  jsonEditorOptions = {
    theme: 'vs',
    language: 'json',
    minimap: { enabled: false },
    lineNumbers: 'on' as const,
    folding: true,
    automaticLayout: true,
    scrollBeyondLastLine: false,
    wordWrap: 'on' as const,
  };
  mappingEditorOptions = {
    ...this.jsonEditorOptions,
    language: 'text',
    readOnly: true,
  };
  certificateRefreshNonce = 0;
  integrationMappingLoading = false;
  integrationMappingError = '';
  integrationMappingJson = '';
  integrationPreviewInput = '{\n  \n}';
  integrationPreviewLoading = false;
  integrationPreviewError = '';
  integrationPreviewJson = '';
  integrationEndpointAdapterCode = '';
  integrationEndpointDetailsLoading = false;
  integrationEndpointDetailsError = '';

  private sub = new Subscription();
  private lastSelectedEndpointCode = '';

  constructor(
    private cdr: ChangeDetectorRef,
    @Optional() private http?: HttpClient
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Rebuild only when pointed at a (genuinely) different action. We DON'T write
    // to the store while editing (the inspector commits on Save/close), so the
    // @Input reference is stable during edits — no rebuild, no overlay teardown.
    if (!this.action) return;
    if (!changes['action'] && !changes['formFields']) return;
    this.model = {
      actionType: this.action.actionType,
      args: applyWorkflowActionArgDefaults(
        this.action.actionType,
        prepareWorkflowActionArgsForForm(clone(this.action.args) ?? {})
      ),
    };
    this.buildForm();
  }

  private buildForm(): void {
    // Keep our actionType in the model (drives the hide-expressions) but don't render
    // the actionType picker itself — the action's type is fixed in this card.
    this.fields = getStateActionFields([]).filter(f => f.key !== 'actionType');
    this.applyNotificationConditionals(this.fields);
    this.applyOfficeConditionals(this.fields);
    this.applyIntegrationEndpointDropdown(this.fields);
    this.applyCertificateTemplateDropdown(this.fields);
    this.form = new FormGroup({});
    // hideBuilderWrapper: drop the field-builder toolbar; ALLOW_EDITING_ALL_FIELDS:
    // the shared FormStateService can be in a stale "RFA" state which the readonly
    // wrappers honour (pointer-events:none) — this is the lib's escape hatch.
    this.options = {
      formState: {
        _rootModel: this.model,
        hideBuilderWrapper: true,
        ALLOW_EDITING_ALL_FIELDS: true,
      },
    };

    // We don't write to the store on each edit (no race / no overlay teardown), but
    // the reused ng-select needs a change-detection pass after a value change to
    // close/sync its panel — the store round-trip used to provide that. Nudge CD
    // locally instead (no persistence; the inspector commits on Save).
    this.sub.unsubscribe();
    this.sub = new Subscription();
    this.sub.add(
      this.form.valueChanges.subscribe(() => {
        this.syncSelectedOfficeLevel();
        this.syncCertificateExpiryState();
        this.syncIntegrationEndpointState();
        setTimeout(() => this.cdr.detectChanges(), 0);
      })
    );
    this.syncIntegrationEndpointState();
    this.syncCertificateExpiryState();
  }

  private syncSelectedOfficeLevel(): void {
    const officeLevel = selectedOfficeLevel(this.model.args['officeId']);
    if (!officeLevel) return;
    this.setArgValue('officeLevel', officeLevel);
  }

  private setArgValue(key: string, value: unknown): void {
    if (this.model.args[key] !== value) {
      this.model.args[key] = value;
    }
    const control =
      this.form.get(['args', key]) || this.form.get(`args.${key}`);
    if (control && control.value !== value) {
      control.setValue(value, { emitEvent: false });
    }
  }

  private booleanArgValue(key: string): boolean {
    const value = this.currentArgValue(key);
    return value === true || value === 'true';
  }

  get certificateExpiryEnabled(): boolean {
    return (
      this.action?.actionType === 'GENERATE_CERTIFICATE' &&
      this.booleanArgValue('certificateExpires')
    );
  }

  private syncCertificateExpiryState(): void {
    if (this.action?.actionType !== 'GENERATE_CERTIFICATE') return;
    if (!this.certificateExpiryEnabled) {
      this.setArgValue('certificateExpirationDays', 0);
      return;
    }
    const expirationDays = Number(
      this.currentArgValue('certificateExpirationDays') ?? 0
    );
    if (!Number.isFinite(expirationDays) || expirationDays <= 0) {
      this.setArgValue('certificateExpirationDays', 365);
    }
  }

  get showCertificateRefresh(): boolean {
    return this.action?.actionType === 'GENERATE_CERTIFICATE';
  }

  get showIntegrationMappingPanel(): boolean {
    return (
      [
        'INTEGRATION',
        'INTEGRATION_UPDATE',
        'URL_CALL',
        'FETCH_ID_PHOTO',
      ].includes(this.action?.actionType) &&
      !!this.selectedEndpointCode &&
      this.isSelectedEndpointJsonataCompatible
    );
  }

  get selectedEndpointCode(): string {
    return this.endpointCodeFromValue(this.currentArgValue('endpointCode'));
  }

  private endpointCodeFromValue(value: unknown): string {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return String(
        (value as Record<string, unknown>)['code'] ??
          (value as Record<string, unknown>)['endpointCode'] ??
          ''
      ).trim();
    }
    return String(value ?? '').trim();
  }

  private certificateTemplateOptionFromArgs(): Record<string, unknown> | null {
    if (this.action?.actionType !== 'GENERATE_CERTIFICATE') return null;
    const value = this.model.args['certificateTemplateCode'];
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const template = value as Record<string, unknown>;
      const code = selectedCertificateCode(template);
      if (code) return template;
    }
    return null;
  }

  private currentArgValue(key: string): unknown {
    const control =
      this.form.get(['args', key]) || this.form.get(`args.${key}`);
    return control ? control.value : this.model.args[key];
  }

  get isSelectedEndpointJsonataCompatible(): boolean {
    return this.integrationEndpointAdapterCode.toUpperCase() === 'PMAPPING';
  }

  get canEvaluateIntegrationMapping(): boolean {
    return (
      !!this.selectedEndpointCode &&
      !!this.integrationMappingJson.trim() &&
      this.isSelectedEndpointJsonataCompatible &&
      !this.integrationPreviewLoading &&
      !this.integrationEndpointDetailsLoading
    );
  }

  get integrationMappingUnknownVariables(): string[] {
    if (!this.integrationMappingJson.trim()) return [];
    const availableKeys = this.availableIntegrationPayloadKeys();
    return integrationMappingInputPaths(this.integrationMappingJson)
      .map(path => path[0])
      .filter(Boolean)
      .filter((key, index, all) => all.indexOf(key) === index)
      .filter(key => !availableKeys.has(key));
  }

  refreshCertificateTemplates(): void {
    this.certificateRefreshNonce += 1;
    this.buildForm();
    setTimeout(() => this.cdr.detectChanges(), 0);
  }

  fetchIntegrationMapping(): void {
    const endpointCode = this.selectedEndpointCode;
    if (!endpointCode) {
      this.integrationMappingError = 'Select an endpoint first.';
      return;
    }
    if (!this.http) {
      this.integrationMappingError = 'HTTP client is not available.';
      return;
    }
    if (
      this.integrationEndpointAdapterCode &&
      !this.isSelectedEndpointJsonataCompatible
    ) {
      return;
    }

    this.integrationMappingLoading = true;
    this.integrationMappingError = '';
    this.integrationPreviewJson = '';
    this.syncIntegrationEndpointState();
    this.http
      .get<Record<string, unknown>>(integrationEndpointMappingUrl(endpointCode))
      .pipe(
        finalize(() => {
          this.integrationMappingLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          const data = this.unwrapResponseData(response);
          const mapping =
            typeof data?.['mapping'] === 'string' ? data['mapping'] : '';
          this.integrationMappingJson =
            mapping || JSON.stringify(data ?? {}, null, 2);
          if (mapping) {
            this.integrationPreviewInput =
              buildIntegrationMappingSamplePayload(mapping);
          }
        },
        error: error => {
          this.integrationMappingError =
            error?.error?.message ||
            error?.error?.responseMessage ||
            'Could not fetch endpoint mapping. Refresh and try again.';
        },
      });
  }

  async testIntegrationMapping(): Promise<void> {
    const endpointCode = this.selectedEndpointCode;
    if (!endpointCode) {
      this.integrationPreviewError = 'Select an endpoint first.';
      return;
    }
    if (!this.integrationMappingJson.trim()) {
      this.integrationPreviewError = 'Fetch the endpoint mapping first.';
      return;
    }
    if (!this.isSelectedEndpointJsonataCompatible) {
      return;
    }
    let payload: Record<string, unknown>;
    try {
      const parsed = JSON.parse(this.integrationPreviewInput || '{}');
      payload =
        parsed && typeof parsed === 'object' && !Array.isArray(parsed)
          ? parsed
          : {};
    } catch {
      this.integrationPreviewError = 'Sample payload must be valid JSON.';
      return;
    }

    this.integrationPreviewLoading = true;
    this.integrationPreviewError = '';
    try {
      const expression = jsonata(this.integrationMappingJson);
      const result = await Promise.resolve(expression.evaluate(payload));
      this.integrationPreviewJson = this.stringifyJsonataResult(result);
    } catch (error: unknown) {
      this.integrationPreviewJson = '';
      this.integrationPreviewError =
        error instanceof Error
          ? error.message
          : 'Could not evaluate endpoint mapping.';
    } finally {
      this.integrationPreviewLoading = false;
      this.cdr.markForCheck();
    }
  }

  private syncIntegrationEndpointState(): void {
    const endpointCode = this.selectedEndpointCode;
    if (endpointCode === this.lastSelectedEndpointCode) return;
    this.lastSelectedEndpointCode = endpointCode;
    this.integrationMappingJson = '';
    this.integrationMappingError = '';
    this.integrationPreviewJson = '';
    this.integrationPreviewError = '';
    this.integrationEndpointAdapterCode = '';
    this.integrationEndpointDetailsError = '';
    if (endpointCode) this.fetchIntegrationEndpointDetails(endpointCode);
  }

  private fetchIntegrationEndpointDetails(endpointCode: string): void {
    if (!this.http) {
      this.integrationEndpointDetailsError = 'HTTP client is not available.';
      return;
    }

    this.integrationEndpointDetailsLoading = true;
    this.http
      .get<Record<string, unknown>>(integrationEndpointDetailUrl(endpointCode))
      .pipe(
        finalize(() => {
          this.integrationEndpointDetailsLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: response => {
          const data = this.unwrapResponseData(response);
          this.integrationEndpointAdapterCode = String(
            data?.['adapterCode'] ?? ''
          ).trim();
          if (this.isSelectedEndpointJsonataCompatible) {
            this.fetchIntegrationMapping();
          }
        },
        error: error => {
          this.integrationEndpointAdapterCode = '';
          this.integrationEndpointDetailsError =
            error?.error?.message ||
            error?.error?.responseMessage ||
            'Could not fetch endpoint details.';
        },
      });
  }

  private stringifyJsonataResult(result: unknown): string {
    if (typeof result === 'string') {
      try {
        return JSON.stringify(JSON.parse(result), null, 2);
      } catch {
        return result;
      }
    }
    return JSON.stringify(result ?? null, null, 2);
  }

  private unwrapResponseData(
    response: Record<string, unknown> | null | undefined
  ): Record<string, unknown> | null {
    if (!response) return null;
    const data = response['data'] ?? response['response'] ?? response;
    return data && typeof data === 'object' && !Array.isArray(data)
      ? (data as Record<string, unknown>)
      : { value: data };
  }

  /**
   * V1's notification fields rely on the classic editor's field ancestry in
   * string hide-expressions. V2 renders only the action args form, so point
   * those conditionals at the local action model while keeping the shared field
   * definitions and custom dropdown dataset config.
   */
  private applyNotificationConditionals(fields: FormlyFieldConfig[]): void {
    const actionType = this.action?.actionType || '';
    if (!NOTIFICATION_BACKED_ACTIONS.includes(actionType)) return;
    const args = fields.find(f => f.key === 'args');
    if (!args?.fieldGroup) return;

    const isNotificationBacked = (): boolean =>
      NOTIFICATION_BACKED_ACTIONS.includes(this.model.actionType);

    const notificationType = args.fieldGroup.find(
      field => field.key === 'notificationType'
    );
    if (notificationType) {
      notificationType.expressions = {
        ...(notificationType.expressions || {}),
        hide: () => !isNotificationBacked(),
      };
    }

    const staticTemplate = args.fieldGroup.find(
      field => field.key === 'staticTemplate'
    );
    if (staticTemplate) {
      staticTemplate.expressions = {
        ...(staticTemplate.expressions || {}),
        hide: () =>
          !isNotificationBacked() ||
          this.model.args['notificationType'] !== 'STATIC',
      };
    }

    const dynamicTemplate = args.fieldGroup.find(
      field => field.key === 'dynamicTemplate'
    );
    if (dynamicTemplate) {
      dynamicTemplate.expressions = {
        ...(dynamicTemplate.expressions || {}),
        hide: () =>
          !isNotificationBacked() ||
          this.model.args['notificationType'] !==
            'NOTIFICATION_ENGINE_TEMPLATE',
      };
    }
  }

  /**
   * Office-assignment fields are conditional on the chosen assignment type
   * (rules mirror office-assignment-form): a FIXED office needs Office ID + Level;
   * an in-application office needs the Form Field Key. We only add `hide`
   * expressions to a FRESH copy of the reused fields, so v1 stays untouched.
   */
  private applyOfficeConditionals(fields: FormlyFieldConfig[]): void {
    const actionType = this.action?.actionType || '';
    if (!OFFICE_BACKED_ACTIONS.includes(actionType)) return;
    const args = fields.find(f => f.key === 'args');
    if (!args?.fieldGroup) return;
    const isOfficeBacked = (): boolean =>
      OFFICE_BACKED_ACTIONS.includes(this.model.actionType);
    const officeType = (): string =>
      String(this.model.args['officeAssignmentType'] || '');
    const officeAssignmentType = args.fieldGroup.find(
      x => x.key === 'officeAssignmentType'
    );
    if (officeAssignmentType) {
      officeAssignmentType.expressions = {
        ...(officeAssignmentType.expressions || {}),
        hide: () => !isOfficeBacked(),
      };
    }
    const setHide = (key: string, showWhen: () => boolean) => {
      const f = args.fieldGroup!.find(x => x.key === key);
      if (f)
        f.expressions = {
          ...(f.expressions || {}),
          hide: () => !(isOfficeBacked() && showWhen()),
        };
    };
    setHide('officeId', () => officeType() === 'CODE_FIXED_OFFICE');
    setHide(
      'officeLevel',
      () =>
        officeType() === 'CODE_FIXED_OFFICE' ||
        officeType() === 'CODE_FROM_LOCATION' ||
        officeType() === 'CODE_APPLICATION_FIELD_CODE'
    );
    setHide(
      'formFieldKey',
      () =>
        officeType() === 'CODE_IN_APPLICATION_OFFICE' ||
        officeType() === 'CODE_FROM_LOCATION' ||
        officeType() === 'CODE_APPLICATION_FIELD_CODE'
    );
    setHide('formFieldKeys', () => false);
    setHide('fixedOfficeCode', () => false);
    setHide(
      'officeAssignmentExpression',
      () => officeType() === 'CODE_FROM_AN_EXPRESSION'
    );

    // Turn the raw Office ID input into a backend office picker (offices by level
    // for this org) — same endpoint office-assignment-form uses. Refetches when
    // the chosen level changes.
    const officeId = args.fieldGroup.find(x => x.key === 'officeId');
    if (officeId && this.organizationId) {
      const selectedOffice = this.currentOfficeOptionFromArgs();
      if (selectedOffice && typeof this.model.args['officeId'] === 'string') {
        this.model.args['officeId'] = selectedOffice;
      }
      officeId.type = 'customdropdown';
      officeId.props = {
        ...(officeId.props || {}),
        label: 'Office',
        placeholder: 'Select an office',
        bindLabel: 'name',
        bindValue: undefined,
        useBaseUrl: true,
        options: selectedOffice ? [selectedOffice] : [],
        dataset: {
          url: '/officer/v1/offices/filter',
          httpMethod: 'GET',
          dataField: 'data.content',
          params: {
            page: '0',
            size: '100',
            organisationId: this.organizationId,
            level: (this.model.args['officeLevel'] as string) || 'LEVEL_1',
          },
        },
      };
      officeId.expressions = {
        ...(officeId.expressions || {}),
        'props.dataset.params.level':
          "field?.parent?.model?.officeLevel || 'LEVEL_1'",
      };
    }

    const officeLevel = args.fieldGroup.find(x => x.key === 'officeLevel');
    if (officeLevel) {
      const hasSelectedOfficeLevel = (): boolean =>
        !!selectedOfficeLevel(this.model.args['officeId']);
      officeLevel.props = {
        ...(officeLevel.props || {}),
        label: 'Office level',
        readonly:
          officeType() === 'CODE_FIXED_OFFICE' && hasSelectedOfficeLevel(),
        disabled: false,
        placeholder:
          officeType() === 'CODE_FIXED_OFFICE' && hasSelectedOfficeLevel()
            ? 'Filled from selected office'
            : 'Office level, for example LEVEL_1',
        description:
          officeType() === 'CODE_FIXED_OFFICE' && hasSelectedOfficeLevel()
            ? 'This is filled from the selected office and saved with the workflow.'
            : 'If the selected office does not include a level, enter the level manually.',
      };
      officeLevel.expressions = {
        ...(officeLevel.expressions || {}),
        'props.readonly': () =>
          officeType() === 'CODE_FIXED_OFFICE' && hasSelectedOfficeLevel(),
        'props.placeholder': () =>
          officeType() === 'CODE_FIXED_OFFICE' && hasSelectedOfficeLevel()
            ? 'Filled from selected office'
            : 'Office level, for example LEVEL_1',
        'props.description': () =>
          officeType() === 'CODE_FIXED_OFFICE' && hasSelectedOfficeLevel()
            ? 'This is filled from the selected office and saved with the workflow.'
            : 'If the selected office does not include a level, enter the level manually.',
      };
    }

    const formFieldOptions = this.formFieldOptions();
    const formFieldKey = args.fieldGroup.find(x => x.key === 'formFieldKey');
    if (formFieldKey && formFieldOptions.length) {
      formFieldKey.type = 'customdropdown';
      formFieldKey.props = {
        ...(formFieldKey.props || {}),
        label: 'Form field',
        placeholder: 'Select a form field or type a key',
        options: formFieldOptions,
        bindLabel: 'label',
        bindValue: 'key',
      };
    }
  }

  private currentOfficeOptionFromArgs(): Record<string, unknown> | null {
    const office = this.model.args['officeId'];
    if (office && typeof office === 'object' && !Array.isArray(office)) {
      return office as Record<string, unknown>;
    }
    const officeId = selectedOfficeId(office);
    if (!officeId) return null;
    return {
      id: officeId,
      name:
        String(this.model.args['officeName'] ?? '').trim() ||
        String(this.model.args['officeCode'] ?? '').trim() ||
        officeId,
      code: this.model.args['officeCode'] ?? null,
      officeLevel: this.model.args['officeLevel'] ?? null,
    };
  }

  private applyIntegrationEndpointDropdown(fields: FormlyFieldConfig[]): void {
    if (
      ![
        'INTEGRATION',
        'INTEGRATION_UPDATE',
        'URL_CALL',
        'FETCH_ID_PHOTO',
      ].includes(this.action?.actionType)
    ) {
      return;
    }

    const args = fields.find(f => f.key === 'args');
    if (!args?.fieldGroup) return;
    const endpointCode = args.fieldGroup.find(
      field => field.key === 'endpointCode'
    );
    if (!endpointCode) return;

    endpointCode.type = 'customdropdownpaginated';
    const selectedEndpointCode = this.endpointCodeFromValue(
      this.model.args['endpointCode']
    );
    endpointCode.props = {
      ...(endpointCode.props || {}),
      label: 'Endpoint',
      placeholder: 'Select an integration endpoint',
      bindLabel: 'name',
      bindValue: 'code',
      refreshable: true,
      selectClass: 'iwb-template-select',
      itemFilter: (item: Record<string, unknown>) =>
        String(item?.['adapterType'] ?? '').toUpperCase() === 'PUSH',
      optionFn: (item: Record<string, unknown>) => {
        const name = String(item?.['name'] ?? item?.['code'] ?? '');
        const adapterCode = String(item?.['adapterCode'] ?? '').trim();
        return adapterCode ? `${name} · ${adapterCode}` : name;
      },
      useBaseUrl: true,
      options: selectedEndpointCode
        ? [{ code: selectedEndpointCode, name: selectedEndpointCode }]
        : [],
      dataset: {
        url: '/integration/v1/endpoints',
        httpMethod: 'GET',
        dataField: 'data.content',
        pagination: true,
        searching: true,
        searchField: 'query',
        params: {
          page: '0',
          size: '10',
          isActive: true,
          query: 'PUSH',
        },
      },
    };
  }

  private applyCertificateTemplateDropdown(fields: FormlyFieldConfig[]): void {
    if (this.action?.actionType !== 'GENERATE_CERTIFICATE') return;
    const args = fields.find(f => f.key === 'args');
    if (!args?.fieldGroup) return;
    const templateCode = args.fieldGroup.find(
      field => field.key === 'certificateTemplateCode'
    );
    if (!templateCode) return;

    templateCode.type = 'customdropdownpaginated';
    const selectedTemplate = this.certificateTemplateOptionFromArgs();
    templateCode.props = {
      ...(templateCode.props || {}),
      label: 'Certificate template',
      placeholder: 'Search certificate templates',
      bindLabel: 'certificateTemplateName',
      bindValue: undefined,
      useBaseUrl: true,
      options: selectedTemplate ? [selectedTemplate] : [],
      dataset: {
        url: '/document-generation/v1/certificate-templates',
        httpMethod: 'GET',
        dataField: 'data.content',
        pagination: true,
        searching: true,
        searchField: 'search',
        params: {
          page: '0',
          size: '10',
          refresh: String(this.certificateRefreshNonce),
          ...(this.organizationId
            ? { organizationId: this.organizationId }
            : {}),
        },
      },
      description:
        'Select an existing certificate template, or use Design New in the certificate designer.',
    };

    if (!args.fieldGroup.some(field => field.key === 'certificateDesignNew')) {
      const idx = args.fieldGroup.indexOf(templateCode);
      args.fieldGroup.splice(idx + 1, 0, {
        key: 'certificateDesignNew',
        template:
          '<div class="iwb-certificate-design-new"><a class="btn" target="_blank" rel="noopener noreferrer" href="../certificate/new"><i class="fa-solid fa-plus"></i> Design New</a></div>',
        expressions: {
          hide: "!(field?.parent?.parent?.model?.actionType === 'GENERATE_CERTIFICATE')",
        },
      });
    }

    const moveAfter = (
      field: FormlyFieldConfig | undefined,
      afterKey: string
    ): void => {
      if (!field) return;
      const currentIndex = args.fieldGroup!.indexOf(field);
      if (currentIndex >= 0) args.fieldGroup!.splice(currentIndex, 1);
      const afterIndex = args.fieldGroup!.findIndex(
        item => item.key === afterKey
      );
      args.fieldGroup!.splice(
        afterIndex >= 0 ? afterIndex + 1 : args.fieldGroup!.length,
        0,
        field
      );
    };

    const certificateName = args.fieldGroup.find(
      field => field.key === 'certificateName'
    );
    moveAfter(certificateName, 'certificateDesignNew');

    if (!args.fieldGroup.some(field => field.key === 'certificateExpires')) {
      const nameIndex = args.fieldGroup.indexOf(certificateName!);
      args.fieldGroup.splice(nameIndex >= 0 ? nameIndex + 1 : 0, 0, {
        key: 'certificateExpires',
        type: 'checkbox',
        defaultValue: false,
        props: {
          label: 'Certificate expires',
          description:
            'Enable this when the generated certificate should expire and applicants should receive expiry reminders.',
        },
        expressions: {
          hide: "!(field?.parent?.parent?.model?.actionType === 'GENERATE_CERTIFICATE')",
        },
      });
    }

    const reminderNotifications = args.fieldGroup.find(
      field => field.key === 'reminderNotifications'
    );
    if (reminderNotifications) {
      reminderNotifications.expressions = {
        ...(reminderNotifications.expressions || {}),
        hide: () => true,
      };
    }

    if (certificateName) {
      certificateName.props = {
        ...(certificateName.props || {}),
        readonly: true,
        placeholder: 'Filled from selected certificate template',
      };
      certificateName.expressions = {
        ...(certificateName.expressions || {}),
        'model.certificateName': () => {
          const template = this.model.args['certificateTemplateCode'];
          if (
            template &&
            typeof template === 'object' &&
            !Array.isArray(template)
          ) {
            const code = selectedCertificateCode(
              template as Record<string, unknown>
            );
            return selectedCertificateName(
              template as Record<string, unknown>,
              code || ''
            );
          }
          return this.model.args['certificateName'] || '';
        },
      };
    }

    const expirationDays = args.fieldGroup.find(
      field => field.key === 'certificateExpirationDays'
    );
    if (expirationDays) {
      expirationDays.props = {
        ...(expirationDays.props || {}),
        label: 'Expiration days',
        required: this.certificateExpiryEnabled,
        defaultRequired: false,
        description:
          'Number of days after issue before the certificate expires.',
      };
      expirationDays.expressions = {
        ...(expirationDays.expressions || {}),
        hide: () => !this.certificateExpiryEnabled,
        'props.required': () => this.certificateExpiryEnabled,
      };
    }

    const reminderOffsetDays = args.fieldGroup.find(
      field => field.key === 'reminderOffsetDays'
    );
    if (reminderOffsetDays) {
      reminderOffsetDays.props = {
        ...(reminderOffsetDays.props || {}),
        label: 'Reminder before expiry',
        description:
          'How many days before certificate expiry the reminder should be sent.',
      };
      reminderOffsetDays.expressions = {
        ...(reminderOffsetDays.expressions || {}),
        hide: () => !this.certificateExpiryEnabled,
      };
    }
  }

  private formFieldOptions(): Array<{ key: string; label: string }> {
    return extractNotificationVariables(this.formFields)
      .filter(variable => variable.group === 'FORM Values')
      .map(variable => {
        const key =
          variable.value.replace(/^\$\{/, '').replace(/!?}$/, '').trim() ||
          variable.label;
        return { key, label: variable.label || key };
      });
  }

  private availableIntegrationPayloadKeys(): Set<string> {
    return new Set(
      extractNotificationVariables(this.formFields).map(variable =>
        variable.value.replace(/^\$\{/, '').replace(/!?}$/, '').trim()
      )
    );
  }

  /** Switch between the rich form and a raw-JSON editor for this one action. */
  setMode(json: boolean): void {
    if (json === this.jsonMode) return;
    if (json) {
      this.jsonDraft = JSON.stringify(this.model.args ?? {}, null, 2);
      this.jsonError = '';
      this.jsonMode = true;
    } else {
      // leaving JSON → parse it back into the form model and rebuild the form
      try {
        const parsed = JSON.parse(this.jsonDraft || '{}');
        this.model = {
          ...this.model,
          args: parsed && typeof parsed === 'object' ? parsed : {},
        };
        this.buildForm();
        this.jsonError = '';
        this.jsonMode = false;
      } catch {
        this.jsonError = 'Invalid JSON — fix it or stay in JSON mode.';
      }
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  ngAfterViewInit(): void {
    // The reused dropdown components need a CD pass after view init to wire up (mirrors v1).
    setTimeout(() => this.cdr.detectChanges(), 60);
  }

  /** Current (locally edited) args for this action — read by the inspector on Save. */
  currentArgs(): Record<string, unknown> | null {
    let args = this.currentFormArgs();
    if (this.jsonMode) {
      try {
        const p = JSON.parse(this.jsonDraft || '{}');
        if (p && typeof p === 'object') args = p;
      } catch {
        /* keep last good */
      }
    }
    return args && Object.keys(args).length
      ? normalizeWorkflowActionArgs(args, this.model.actionType)
      : null;
  }

  private currentFormArgs(): Record<string, unknown> {
    const formArgs = this.form.get('args')?.value;
    return formArgs && typeof formArgs === 'object' && !Array.isArray(formArgs)
      ? { ...this.model.args, ...(formArgs as Record<string, unknown>) }
      : this.model.args;
  }
}
